/**
 * LiveMap - Real-time GPS tracking map for active medics
 * Uses Leaflet with OpenStreetMap tiles
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  MapPin, 
  Users, 
  RefreshCw, 
  Settings,
  Clock,
  Navigation,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { trackingAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

// Fix for default marker icon paths when bundling leaflet via webpack/CRA.
// Without this the default markers 404 and the map appears empty.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Default map center (London, UK)
const DEFAULT_CENTER = { lat: 51.5074, lng: -0.1278 };
const DEFAULT_ZOOM = 10;

// Auto-refresh interval (30 seconds)
const REFRESH_INTERVAL = 30000;

// Dormant threshold: an in-progress shift that's been checked in without
// a check-out for longer than this is flagged on the map and in the
// sidebar so admins can chase it up (iter71.2 — Dormant Shift Badge).
const DORMANT_AFTER_HOURS = 8;

const hoursSinceCheckIn = (checkedInAt) => {
  if (!checkedInAt) return 0;
  const t = new Date(checkedInAt).getTime();
  if (Number.isNaN(t)) return 0;
  return (Date.now() - t) / 36e5;
};

const isDormant = (medic) =>
  hoursSinceCheckIn(medic?.checked_in_at) >= DORMANT_AFTER_HOURS;

export default function LiveMap() {
  const { isAdmin } = useAuth();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  // Leaflet.markercluster group that holds all shift pins. Markers are
  // added here instead of directly to the map so dense clusters (e.g.
  // multiple shifts in central London) collapse into a numbered badge
  // that expands on click/zoom (iter71.1 — Pin Clustering).
  const clusterRef = useRef(null);
  // Keeps the latest activeMedics accessible inside Leaflet event closures
  // so marker clicks always see fresh data (not the snapshot from the render
  // when the marker was created).
  const activeMedicsRef = useRef([]);
  // Tracks whether we've already auto-fit the map to pin bounds. We only do
  // this once on initial load so later 30s auto-refreshes don't yank the
  // user's manual zoom/pan back.
  const hasFittedBoundsRef = useRef(false);
  
  const [activeMedics, setActiveMedics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedMedic, setSelectedMedic] = useState(null);
  const [leafletLoaded, setLeafletLoaded] = useState(true);
  
  // Geofence settings (admin only)
  const [showSettings, setShowSettings] = useState(false);
  const [geofenceSettings, setGeofenceSettings] = useState({
    enabled: true,
    radius_meters: 500
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Initialize map (Leaflet is bundled locally, so always available)
  useEffect(() => {
    if (loading || !mapRef.current || mapInstanceRef.current) return;

    // Create map
    const map = L.map(mapRef.current).setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM);
    
    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);

    // Marker cluster group: groups nearby shift pins into a numbered
    // badge that expands when the user zooms or taps the cluster.
    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 50,
      iconCreateFunction: (c) => {
        const count = c.getChildCount();
        return L.divIcon({
          html: `
            <div style="
              background: #1ABC9C;
              color: white;
              width: 40px;
              height: 40px;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 10px rgba(0,0,0,0.35);
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: 700;
              font-family: 'JetBrains Mono', monospace;
              font-size: 14px;
            ">${count}</div>
          `,
          className: 'shift-cluster-icon',
          iconSize: [40, 40],
        });
      },
    });
    map.addLayer(cluster);
    clusterRef.current = cluster;

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        clusterRef.current = null;
      }
    };
  }, [loading]);

  // Fetch active medics
  const fetchActiveMedics = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      const response = await trackingAPI.getActiveMedics();
      setActiveMedics(response.data.medics || []);
      setLastUpdated(new Date());
      
      if (showToast) {
        toast.success(`${response.data.active_count} active medics`);
      }
    } catch (error) {
      console.error('Failed to fetch active medics:', error);
      if (showToast) {
        toast.error('Failed to load active medics');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await trackingAPI.getSettings();
      if (response.data.geofence) {
        setGeofenceSettings(response.data.geofence);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchActiveMedics();
    fetchSettings();
  }, [fetchActiveMedics, fetchSettings]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchActiveMedics();
    }, REFRESH_INTERVAL);
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchActiveMedics]);

  // Update map markers when shifts change. Pins are placed at each
  // in-progress shift's geocoded location (iter71 — Live Map Shift Pins).
  // Falls back to the medic's last GPS ping if the shift has no coords.
  useEffect(() => {
    // Keep ref in sync with latest data for click-handler lookups below.
    activeMedicsRef.current = activeMedics;

    if (!mapInstanceRef.current || !clusterRef.current) return;

    const map = mapInstanceRef.current;
    const cluster = clusterRef.current;

    const pinCoordsFor = (medic) => {
      const c = medic.shift_location_coords;
      if (c && c.lat != null && c.lng != null) return [c.lat, c.lng];
      if (medic.current_location?.latitude && medic.current_location?.longitude) {
        return [medic.current_location.latitude, medic.current_location.longitude];
      }
      return null;
    };

    const buildMarkerIcon = (medic) => {
      const dormant = isDormant(medic);
      const letter = (medic.medic_name || 'U').charAt(0).toUpperCase();
      const bg = dormant ? '#F59E0B' : '#1ABC9C';
      return L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="position:relative;width:32px;height:32px;">
            ${dormant ? '<div class="elara-dormant-ring"></div>' : ''}
            <div style="
              background:${bg};
              width:32px;
              height:32px;
              border-radius:50%;
              border:3px solid white;
              box-shadow:0 2px 8px rgba(0,0,0,0.3);
              display:flex;
              align-items:center;
              justify-content:center;
              color:white;
              font-weight:bold;
              font-size:14px;
              position:relative;
            ">${letter}</div>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -20]
      });
    };

    const buildPopupContent = (medic) => {
      const name = medic.medic_name || 'Unassigned';
      const address = medic.shift_address || medic.shift_location || '';
      const hrs = hoursSinceCheckIn(medic.checked_in_at);
      const dormantLine = isDormant(medic)
        ? `<div style="margin-top:4px;padding:3px 6px;background:#F59E0B;color:#1A1A1A;border-radius:4px;font-weight:600;font-size:11px;">⚠ Dormant — checked in ${hrs.toFixed(1)}h ago</div>`
        : '';
      return `
        <div style="min-width: 180px;">
          <strong>${name}</strong><br/>
          <small>📍 ${medic.shift_location || ''}</small><br/>
          ${address && address !== medic.shift_location ? `<small style="color:#555">${address}</small><br/>` : ''}
          <small>🕐 Checked in: ${format(new Date(medic.checked_in_at), 'HH:mm')}</small>
          ${dormantLine}
        </div>
      `;
    };

    // Remove old markers that are no longer active
    Object.keys(markersRef.current).forEach(shiftId => {
      if (!activeMedics.find(m => m.shift_id === shiftId)) {
        cluster.removeLayer(markersRef.current[shiftId]);
        delete markersRef.current[shiftId];
      }
    });

    // Add/update markers for in-progress shifts
    activeMedics.forEach(medic => {
      const coords = pinCoordsFor(medic);
      if (!coords) return;

      const popupContent = buildPopupContent(medic);
      const key = medic.shift_id;
      if (markersRef.current[key]) {
        // Existing marker: update position, popup and icon (dormancy
        // state may have flipped since last render).
        markersRef.current[key]
          .setLatLng(coords)
          .setPopupContent(popupContent)
          .setIcon(buildMarkerIcon(medic));
      } else {
        const marker = L.marker(coords, { icon: buildMarkerIcon(medic) })
          .bindPopup(popupContent);
        cluster.addLayer(marker);

        // Look up the latest medic from the ref so this handler doesn't
        // trap a stale object from the render that created the marker.
        marker.on('click', () => {
          const latest = activeMedicsRef.current.find(m => m.shift_id === key);
          setSelectedMedic(latest || medic);
        });
        markersRef.current[key] = marker;
      }
    });

    // Fit bounds only on the first load with pins. Subsequent auto-refreshes
    // preserve the user's manual zoom/pan.
    const bounds = activeMedics
      .map(pinCoordsFor)
      .filter(Boolean);

    if (bounds.length > 0 && !hasFittedBoundsRef.current) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
      hasFittedBoundsRef.current = true;
    }
  }, [activeMedics, leafletLoaded]);

  // Save geofence settings
  const saveGeofenceSettings = async () => {    setSavingSettings(true);
    try {
      await trackingAPI.updateGeofenceSettings(geofenceSettings);
      toast.success('Geofence settings saved');
      setShowSettings(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Count of in-progress shifts that passed the dormancy threshold —
  // surfaced in a stat card and on each sidebar entry.
  const dormantCount = activeMedics.filter(isDormant).length;

  return (
    <div className="space-y-6" data-testid="live-map-container">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#F2F2F2] font-['Manrope']">
            Live Tracking Map
          </h1>
          <p className="text-[#A1A1AA] text-sm">
            In-progress shifts (checked-in, not yet checked-out)
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Last updated */}
          {lastUpdated && (
            <span className="text-xs text-[#A1A1AA]">
              Updated: {format(lastUpdated, 'HH:mm:ss')}
            </span>
          )}
          
          {/* Auto-refresh toggle */}
          <div className="flex items-center gap-2">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
            />
            <Label htmlFor="auto-refresh" className="text-sm text-[#A1A1AA]">
              Auto-refresh
            </Label>
          </div>
          
          {/* Manual refresh */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchActiveMedics(true)}
            disabled={refreshing}
            className="border-white/10 text-[#A1A1AA] hover:text-[#F2F2F2]"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          {/* Settings (Admin only) */}
          {isAdmin() && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
              className="border-white/10 text-[#A1A1AA] hover:text-[#F2F2F2]"
              data-testid="settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Geofence
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <Users className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-xs text-[#A1A1AA]">In-Progress Shifts</p>
                <p className="text-xl font-bold text-[#1ABC9C] font-['JetBrains_Mono']">
                  {activeMedics.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <Navigation className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-xs text-[#A1A1AA]">Geofence</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {geofenceSettings.enabled ? `${geofenceSettings.radius_meters}m` : 'Off'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card
          className={`bg-[#2B2B2B] ${dormantCount > 0 ? 'border-[#F59E0B]/40' : 'border-white/5'}`}
          data-testid="dormant-stat-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${dormantCount > 0 ? 'bg-[#F59E0B]/15' : 'bg-[#1ABC9C]/15'}`}>
                <AlertTriangle className={`w-5 h-5 ${dormantCount > 0 ? 'text-[#F59E0B]' : 'text-[#1ABC9C]'}`} />
              </div>
              <div>
                <p className="text-xs text-[#A1A1AA]">Dormant (&gt;{DORMANT_AFTER_HOURS}h)</p>
                <p className={`text-xl font-bold font-['JetBrains_Mono'] ${dormantCount > 0 ? 'text-[#F59E0B]' : 'text-[#F2F2F2]'}`}>
                  {dormantCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <Clock className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-xs text-[#A1A1AA]">Refresh Rate</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {autoRefresh ? '30s' : 'Manual'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Geofence Settings Panel (Admin only) */}
      {showSettings && isAdmin() && (
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-[#F2F2F2] font-['Manrope']">
              Geofence Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="geofence-enabled"
                  checked={geofenceSettings.enabled}
                  onCheckedChange={(checked) => 
                    setGeofenceSettings(prev => ({ ...prev, enabled: checked }))
                  }
                />
                <Label htmlFor="geofence-enabled" className="text-[#A1A1AA]">
                  Require location verification for check-in
                </Label>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-[#A1A1AA]">Radius (meters)</Label>
                <Input
                  type="number"
                  min={50}
                  max={5000}
                  value={geofenceSettings.radius_meters}
                  onChange={(e) => 
                    setGeofenceSettings(prev => ({ 
                      ...prev, 
                      radius_meters: parseInt(e.target.value) || 500 
                    }))
                  }
                  disabled={!geofenceSettings.enabled}
                  className="w-32 bg-[#1A1A1A] border-white/10"
                />
              </div>
              
              <Button
                onClick={saveGeofenceSettings}
                disabled={savingSettings}
                className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
              >
                {savingSettings ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Save Settings
              </Button>
            </div>
            <p className="text-xs text-[#A1A1AA] mt-2">
              Medics must be within this distance from the shift location to check in.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Map and Sidebar */}
      <div className="grid lg:grid-cols-4 gap-4">
        {/* Map */}
        <Card className="lg:col-span-3 bg-[#2B2B2B] border-white/5 overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-[500px]">
                <Loader2 className="w-8 h-8 text-[#1ABC9C] animate-spin" />
              </div>
            ) : !leafletLoaded ? (
              <div className="flex items-center justify-center h-[500px] text-[#A1A1AA]">
                Loading map...
              </div>
            ) : (
              <div 
                ref={mapRef} 
                className="h-[500px] w-full"
                data-testid="map-container"
                style={{ background: '#1A1A1A' }}
              />
            )}
          </CardContent>
        </Card>

        {/* Active Medics Sidebar */}
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#1ABC9C]" />
              In-Progress Shifts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[440px] overflow-y-auto">
            {activeMedics.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-10 h-10 text-[#A1A1AA] mx-auto mb-2" />
                <p className="text-[#A1A1AA] text-sm">No in-progress shifts right now</p>
              </div>
            ) : (
              activeMedics.map(medic => {
                const dormant = isDormant(medic);
                const hrs = hoursSinceCheckIn(medic.checked_in_at);
                return (
                <div
                  key={medic.shift_id}
                  onClick={() => {
                    setSelectedMedic(medic);
                    const c = medic.shift_location_coords;
                    const target = (c && c.lat != null && c.lng != null)
                      ? [c.lat, c.lng]
                      : (medic.current_location?.latitude
                          ? [medic.current_location.latitude, medic.current_location.longitude]
                          : null);
                    const marker = markersRef.current[medic.shift_id];
                    if (mapInstanceRef.current && target) {
                      // If the marker is inside a cluster, zoom/spiderfy
                      // so its popup can actually open.
                      if (clusterRef.current && marker) {
                        clusterRef.current.zoomToShowLayer(marker, () => marker.openPopup());
                      } else {
                        mapInstanceRef.current.setView(target, 15);
                        marker?.openPopup();
                      }
                    }
                  }}
                  className={`
                    p-3 rounded-lg cursor-pointer transition-colors border
                    ${selectedMedic?.shift_id === medic.shift_id
                      ? 'bg-[#1ABC9C]/20 border-[#1ABC9C]/30'
                      : dormant
                        ? 'bg-[#F59E0B]/10 border-[#F59E0B]/30 hover:bg-[#F59E0B]/15'
                        : 'bg-[#1A1A1A] border-transparent hover:bg-[#1A1A1A]/70'}
                  `}
                  data-testid={`shift-pin-card-${medic.shift_id}`}
                  data-dormant={dormant ? 'true' : 'false'}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm ${dormant ? 'bg-[#F59E0B]' : 'bg-[#1ABC9C]'}`}>
                      {(medic.medic_name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#F2F2F2] text-sm truncate">
                        {medic.medic_name || 'Unassigned'}
                      </p>
                      <p className="text-xs text-[#A1A1AA] truncate">
                        {medic.shift_location}
                      </p>
                      {medic.shift_address && medic.shift_address !== medic.shift_location && (
                        <p className="text-xs text-[#A1A1AA]/70 truncate">
                          {medic.shift_address}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge className="bg-[#1ABC9C]/15 text-[#1ABC9C] border-0 text-xs">
                          {medic.shift_start_time} - {medic.shift_end_time}
                        </Badge>
                        {dormant && (
                          <Badge
                            className="bg-[#F59E0B] text-[#1A1A1A] border-0 text-xs font-semibold"
                            data-testid="dormant-badge"
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Dormant · {hrs.toFixed(1)}h
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
